import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { tokenRequest } from '../auth/msalConfig';

const useMicrosoftLogin = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAzureLogin = async () => {
    if (isLoading || inProgress !== 'none') {
      // console.log('Login already in progress or loading:', { isLoading, inProgress });
      return { success: false, error: 'Login already in progress' };
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('Starting Azure login process');
      // console.log('Current accounts:', accounts);
      // console.log('InProgress status:', inProgress);

      const currentAccounts = instance.getAllAccounts();
      let account = currentAccounts.length > 0 ? currentAccounts[0] : null;

      if (account) {
        console.log('Found existing account, trying silent token acquisition');
        
        try {
          const response = await instance.acquireTokenSilent({
            ...tokenRequest,
            account: account,
          });

          console.log('Silent token acquisition successful');

          if (response && response.accessToken) {
            // Store tokens in the format your app expects
            const userData = {
              accessToken: response.accessToken,
              user: {
                name: response.account?.name || response.account?.username || 'Microsoft User',
                email: response.account?.username || '',
                id: response.account?.localAccountId || '',
              },
              loginMethod: 'microsoft',
              msalAccount: response.account
            };

            // Store in localStorage in the format your app expects
            localStorage.setItem('user', JSON.stringify(userData));
            localStorage.setItem('access_token', response.accessToken);
            localStorage.setItem('environment', 'MSAL');

            // Set axios headers for immediate use
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.accessToken}`;
            axios.defaults.headers.common.Accept = 'application/json';
            
            setIsLoading(false);
            return { success: true, token: response.accessToken, account: response.account };
          }
        } catch (silentError) {
          console.log('Silent token acquisition failed');
          
          if (silentError.errorCode === 'consent_required' || 
              silentError.errorCode === 'interaction_required' ||
              silentError.errorCode === 'login_required') {
            
            console.log('Interactive login required');
          } else {
            // console.log('Clearing session due to error:', silentError.errorCode);
            clearStoredTokens();
            setError('Session expired. Please login again.');
            setIsLoading(false);
            return { success: false, error: 'Session expired' };
          }
        }
      }

      // Interactive login
      console.log('Attempting interactive login (popup)');

      try {
        const response = await instance.loginPopup({
          ...tokenRequest,
          prompt: "select_account"
        });

        console.log('Interactive login successful');

        if (response && response.accessToken) {
          // Store tokens in the format your app expects
          const userData = {
            accessToken: response.accessToken,
            user: {
              name: response.account?.name || response.account?.username || 'Microsoft User',
              email: response.account?.username || '',
              id: response.account?.localAccountId || '',
            },
            loginMethod: 'microsoft',
            msalAccount: response.account
          };

          // Store in localStorage in the format your app expects
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('access_token', response.accessToken);
          localStorage.setItem('environment', 'MSAL');

          // Set axios headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.accessToken}`;
          axios.defaults.headers.common.Accept = 'application/json';
          
          setIsLoading(false);
          return { success: true, token: response.accessToken, account: response.account };
        } else {
          throw new Error('No access token received');
        }
        
      } catch (interactiveError) {
        console.error('Interactive login failed:', interactiveError);
        clearStoredTokens();
        
        let errorMessage = 'Login failed. Please try again.';
        
        switch (interactiveError.errorCode) {
          case 'user_cancelled':
            errorMessage = 'Login was cancelled';
            break;
          case 'consent_required':
            errorMessage = 'Additional permissions required';
            break;
          case 'interaction_in_progress':
            errorMessage = 'Another login is in progress. Please wait and try again.';
            break;
          case 'popup_window_error':
            errorMessage = 'Popup was blocked. Please allow popups and try again.';
            break;
          default:
            errorMessage = interactiveError.message || errorMessage;
        }
        
        setError(errorMessage);
        setIsLoading(false);
        return { success: false, error: errorMessage };
      }

    } catch (error) {
      console.error('Unexpected Azure login error:', error);
      const errorMessage = 'An unexpected error occurred during login';
      setError(errorMessage);
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  };

  const clearStoredTokens = () => {
    localStorage.removeItem('environment');
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const logout = async () => {
    try {
      clearStoredTokens();
      
      const currentAccounts = instance.getAllAccounts();
      if (currentAccounts.length > 0) {
        await instance.logoutPopup({
          account: currentAccounts[0]
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Function to refresh Microsoft token
  const refreshMicrosoftToken = async () => {
    try {
      const currentAccounts = instance.getAllAccounts();
      if (currentAccounts.length === 0) {
        throw new Error('No accounts found');
      }

      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: currentAccounts[0],
        forceRefresh: true
      });

      if (response && response.accessToken) {
        // Update stored tokens
        const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
        const updatedUser = {
          ...existingUser,
          accessToken: response.accessToken
        };

        localStorage.setItem('user', JSON.stringify(updatedUser));
        localStorage.setItem('access_token', response.accessToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.accessToken}`;

        return response.accessToken;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  };

  return {
    handleAzureLogin,
    refreshMicrosoftToken,
    logout,
    isLoading,
    error,
    setError
  };
};

export default useMicrosoftLogin;